import { FormProvider, useForm, useFormContext } from "react-hook-form";
import type { FieldValues } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  batchTemplateActivityDelayedStartDateOptionsSchema,
  batchTemplateActivityTimeConfigTypeSchema,
  type BatchTemplateActivityFormFields,
  type IBatchTemplateActivityDelayedStartDateOption,
} from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import { TemplateTitleInput } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateTitleInput";
import { TemplateActivityCategoryPicker } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateActivityCategoryPicker";
import { TemplateMeasurementsPicker } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateMeasurementsPicker";
import { TemplateDescriptionInput } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateDescriptionInput";
import { TemplateScheduling } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateScheduling/TemplateScheduling";
import { TemplateDuration } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateDuration/TemplateDuration";
import { TemplateCompetencePicker } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateCompetencePicker";
import { TemplateAddInvoicingCodesAction } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateAddInvoicingCodesAction";
import {
  categorySchema,
  getActivityCategoryConfig,
} from "@medoma/models/activities";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { type IPatientType } from "@medoma/models/patients";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import Checkbox from "@/components/Checkbox/Checkbox";
import styles from "./BatchTemplateActivityForm.module.scss";
import { BatchTemplateActivityInclusionPicker } from "./BatchTemplateActivityInclusionPicker";
import { BatchTemplateActivityStartDateDelayPicker } from "./BatchTemplateActivityStartDateDelayPicker";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import { FormFieldPathPrefixContextProvider } from "@/forms/FormFieldPathPrefixContextProvider";
import type {
  BatchTemplateActivityFormFields as ActivityFields,
  IBatchTemplateActivityTimeConfigType,
} from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import { TemplateFieldValidationContextProvider } from "../../TemplateFieldValidationContextProvider";

export const CreateBatchTemplateActivityForm = ({
  onSave,
  onCancel,
  timeConfigType,
  batchPatientType,
}: {
  onSave: (data: BatchTemplateActivityFormFields) => void;
  onCancel: () => void;
  timeConfigType: IBatchTemplateActivityTimeConfigType;
  batchPatientType: IPatientType;
}) => {
  const methods = useForm<BatchTemplateActivityFormFields>({
    defaultValues: {
      delayedStartDate:
        batchTemplateActivityDelayedStartDateOptionsSchema.enum.none,
    },
  });

  const handleSave = methods.handleSubmit((data) => {
    onSave(data);
  });

  return (
    <FormProvider {...methods}>
      <FormFieldPathPrefixContextProvider prefix={undefined}>
        <BatchTemplateActivityFormContent
          timeConfigType={timeConfigType}
          batchPatientType={batchPatientType}
          onDone={handleSave}
          onCancel={onCancel}
        />
      </FormFieldPathPrefixContextProvider>
    </FormProvider>
  );
};

type BatchTemplateActivityFormContentProps = {
  onDone: () => void;
  onCancel?: () => void;
  timeConfigType: IBatchTemplateActivityTimeConfigType;
  batchPatientType: IPatientType;
};

/**
 * Form content for a batch template activity.
 * Expects to be wrapped in FormProvider and FormFieldPathPrefixContextProvider.
 */
export const BatchTemplateActivityFormContent = ({
  timeConfigType,
  batchPatientType,
  onDone,
  onCancel,
}: BatchTemplateActivityFormContentProps) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
    watch,
    setValue,
  } = useFormContext<FieldValues>();

  const { data: showDurationInUI } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const { organization } = useUserProfile();

  const categoryFieldPath = useFieldPathWithPrefix<ActivityFields>("category");
  const delayedStartDateOffsetFieldPath =
    useFieldPathWithPrefix<ActivityFields>("delayedStartDateOffset");
  const delayedStartDateUnitFieldPath = useFieldPathWithPrefix<ActivityFields>(
    "delayedStartDateUnit",
  );
  const delayedStartDateWeekdayFieldPath =
    useFieldPathWithPrefix<ActivityFields>("delayedStartDateWeekday");
  const doubleStaffingFieldPath =
    useFieldPathWithPrefix<ActivityFields>("doubleStaffing");
  const hiddenFieldPath = useFieldPathWithPrefix<ActivityFields>("hidden");
  const measurementsFieldPath =
    useFieldPathWithPrefix<ActivityFields>("measurements");
  const procedureCodesFieldPath =
    useFieldPathWithPrefix<ActivityFields>("procedureCodes");
  const productCodesFieldPath =
    useFieldPathWithPrefix<ActivityFields>("productCodes");
  const requiredCompetencesFieldPath = useFieldPathWithPrefix<ActivityFields>(
    "requiredCompetences",
  );
  const doubleStaffingError = useFieldErrorWithPrefix<ActivityFields>(
    errors,
    "doubleStaffing",
  );
  const hiddenError = useFieldErrorWithPrefix<ActivityFields>(errors, "hidden");

  const currentCategory = watch(categoryFieldPath);

  const {
    allowsHidden,
    allowsCompetence,
    requiresCompetence,
    allowsInvoicingCode: selectedCategoryAllowsInvoicingCode,
    allowsDuration,
    allowsDoubleStaffing,
  } = currentCategory
    ? getActivityCategoryConfig({
        category: currentCategory,
        activityHasPatient: true,
      })
    : {
        allowsHidden: false,
        allowsCompetence: false,
        requiresCompetence: false,
        allowsInvoicingCode: false,
        allowsDuration: false,
        allowsDoubleStaffing: false,
      };

  const clearDelayedStartDateDependentFields = (
    newValue: IBatchTemplateActivityDelayedStartDateOption,
  ) => {
    if (
      newValue === batchTemplateActivityDelayedStartDateOptionsSchema.enum.none
    ) {
      setValue(delayedStartDateOffsetFieldPath, "");
      setValue(delayedStartDateUnitFieldPath, undefined);
      setValue(delayedStartDateWeekdayFieldPath, undefined);
    }
    if (
      newValue !==
      batchTemplateActivityDelayedStartDateOptionsSchema.enum.weekday
    ) {
      setValue(delayedStartDateWeekdayFieldPath, undefined);
    }
    if (
      newValue !==
      batchTemplateActivityDelayedStartDateOptionsSchema.enum.offset
    ) {
      setValue(delayedStartDateOffsetFieldPath, "");
      setValue(delayedStartDateUnitFieldPath, undefined);
    }
  };

  const clearCategoryDependentFields = () => {
    setValue(procedureCodesFieldPath, []);
    setValue(productCodesFieldPath, []);
    setValue(doubleStaffingFieldPath, false);
    setValue(measurementsFieldPath, []);
    setValue(hiddenFieldPath, false);
    setValue(requiredCompetencesFieldPath, []);
  };

  return (
    <TemplateFieldValidationContextProvider
      value={{ isInFinalValidationPhase: true }}
    >
      <div className={styles.activityForm}>
        {/* Title */}
        <TemplateTitleInput />

        {/* Category */}
        <TemplateActivityCategoryPicker
          onCategoryChange={clearCategoryDependentFields}
          templateIsWithoutPatient={false}
        />

        {/* Measurements (for PatientMeasurementTask) */}
        {currentCategory === categorySchema.enum.PatientMeasurementTask ? (
          <TemplateMeasurementsPicker />
        ) : null}

        {/* Description */}
        <TemplateDescriptionInput />

        {timeConfigType ===
        batchTemplateActivityTimeConfigTypeSchema.enum.individual ? (
          <>
            {/* Delayed start date */}
            <BatchTemplateActivityStartDateDelayPicker
              onDelayedStartDateChange={clearDelayedStartDateDependentFields}
            />

            {/* Scheduling */}
            <TemplateScheduling />
          </>
        ) : null}

        {/* Duration */}
        {showDurationInUI && allowsDuration ? <TemplateDuration /> : null}

        {/* Competence */}
        {allowsCompetence ? (
          <TemplateCompetencePicker
            isRequiredBySelectedCategory={requiresCompetence}
          />
        ) : null}

        {/* Other options */}
        {allowsDoubleStaffing || allowsHidden ? (
          <Checkboxes legend={t`Övrigt`} orientation="horizontal">
            {allowsDoubleStaffing ? (
              <Checkbox
                errorMessage={doubleStaffingError}
                label={{ text: t`Kräver dubbelbemanning` }}
                visualStyle="framed"
                {...register(doubleStaffingFieldPath)}
              />
            ) : null}
            {allowsHidden ? (
              <Checkbox
                errorMessage={hiddenError}
                label={{ text: t`Dölj aktiviteten för patienten` }}
                visualStyle="framed"
                {...register(hiddenFieldPath)}
              />
            ) : null}
          </Checkboxes>
        ) : null}

        {/* Invoicing */}
        {selectedCategoryAllowsInvoicingCode &&
        organizationUsesInvoicingCodes(organization) ? (
          <TemplateAddInvoicingCodesAction
            selectedPatientType={batchPatientType}
          />
        ) : null}

        {/* Batch-specific fields */}
        <BatchTemplateActivityInclusionPicker />

        <div className={styles.actionButtons}>
          <FilledButton type="button" onClick={onDone}>
            <Trans>Klar</Trans>
          </FilledButton>
          {onCancel ? (
            <OutlinedButton type="button" onClick={onCancel}>
              <Trans>Avbryt</Trans>
            </OutlinedButton>
          ) : null}
        </div>
      </div>
    </TemplateFieldValidationContextProvider>
  );
};
